import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from './ToastProvider';
import Icon from './Icon';
import { apiFetch } from '../config/api';
import '../styles/Chat.css';

export const getConvKey = (user1Role, user1Id, user2Role, user2Id) => {
  const a = `${user1Role}-${user1Id}`;
  const b = `${user2Role}-${user2Id}`;
  return [a, b].sort().join('_');
};

const Chat = ({ conversationId, recipientName, recipientRole }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [modal, setModal] = useState(null);

  const messagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastCountRef = useRef(0);

  const load = useCallback(async () => {
    if (!conversationId) return;
    try {
      const data = await apiFetch(`/messages/${conversationId}`);
      setMessages(data || []);
    } catch (err) {
      console.error('Load messages error:', err);
    }
  }, [conversationId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load, conversationId]);

  useEffect(() => {
    if (messages.length > lastCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    lastCountRef.current = messages.length;
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || loading) return;

    const msgData = {
      text: newMessage.trim(),
      senderName: `${user.firstName} ${user.lastName}`,
      senderRole: user.role,
    };

    setLoading(true);
    try {
      await apiFetch(`/messages/${conversationId}`, {
        method: 'POST',
        body: JSON.stringify(msgData),
      });
      setNewMessage('');
      setTimeout(load, 200);
    } catch (err) {
      toast.error(err.message || 'Błąd podczas wysyłania');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConvo = async () => {
    try {
      await apiFetch(`/messages/${conversationId}/delete-all`, {
        method: 'DELETE',
      });
      setMessages([]);
      toast.success('Konwersacja została usunięta');
      setModal(null);
    } catch (err) {
      console.error('Delete conversation error:', err);
      toast.error('Błąd podczas usuwania');
      setModal(null);
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return parts[0][0].toUpperCase();
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-recipient-info">
          <div className="chat-avatar" style={{background: '#6b7280'}}>{getInitials(recipientName)}</div>
          <div className="chat-recipient-details">
            <div className="chat-recipient-name">{recipientName}</div>
            <div className="chat-recipient-role">{recipientRole}</div>
          </div>
        </div>
        <div className="chat-header-actions">
          <button className="chat-action-btn delete" onClick={() => setModal({ type: 'delete' })} title="Usuń konwersację">
            <Icon name="trash" />
          </button>
        </div>
      </div>

      <div className="chat-messages" ref={messagesRef}>
        {messages.length === 0 && (
          <div className="chat-empty">
            <Icon name="chat" size="3x" color="#ccc" />
            <p>Brak wiadomości</p>
            <small>Rozpocznij konwersację poniżej</small>
          </div>
        )}

        {messages.map((msg) => {
          const mine = msg.sender_id === user?.id;
          const avatar = mine 
            ? getInitials(`${user.firstName} ${user.lastName}`)
            : getInitials(msg.sender_name);
          
          return (
            <div key={msg.id} className={`message-wrapper ${mine ? 'mine' : 'theirs'}`}>
              <div 
                className="message-avatar" 
                style={{background: mine ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' : '#6b7280'}}
              >
                {avatar}
              </div>
              <div className={`message-bubble ${mine ? 'mine' : 'theirs'}`}>
                {!mine && <span className="message-sender">{msg.sender_name}</span>}
                <p>{msg.text}</p>
                <span className="message-time">
                  {new Date(msg.created_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-form" onSubmit={handleSend}>
          <input
            className="chat-input"
            placeholder="Wpisz wiadomość..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={loading}
          />
          <button type="submit" className="chat-send-btn" disabled={!newMessage.trim() || loading}>
            <Icon name="send" color="white" />
          </button>
        </form>

      {modal?.type === 'delete' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Usuń konwersację?</h3>
            <p>Czy na pewno chcesz usunąć tę konwersację? Tej operacji nie można cofnąć.</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setModal(null)}>Anuluj</button>
              <button className="btn-confirm btn-danger" onClick={handleDeleteConvo}>Usuń</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
